import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { Textarea } from "@/components/ui/textarea";

export interface ErpFormTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange' | 'size' | 'onInput' | 'rows'> {
  value?: string | number | null; // Replaces modelValue
  placeholder?: string;
  disabled?: boolean;
  readOnly?: boolean;
  rows?: number | string;
  maxRows?: number | string;
  wrap?: string;
  maxLength?: number;
  state?: boolean | null;
  id?: string;
  noResize?: boolean;
  size?: 'sm' | 'lg';
  formatter?: (value: string) => string;
  lazyFormatter?: boolean;
  ariaLabel?: string;
  autoComplete?: string;
  onChange?: (value: string, event: React.ChangeEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
}

export const ErpFormTextarea = forwardRef<HTMLTextAreaElement, ErpFormTextareaProps>(({
  value: modelValue = '',
  placeholder,
  disabled = false,
  readOnly = false,
  rows = 2,
  maxRows: _maxRows,
  wrap = 'soft',
  maxLength,
  state = null,
  id,
  noResize = false,
  size,
  formatter,
  lazyFormatter = false,
  ariaLabel,
  autoComplete,
  onChange,
  onBlur,
  onFocus,
  className,
  ...attrs
}, ref) => {
  const internalRef = useRef<HTMLTextAreaElement>(null);
  useImperativeHandle(ref, () => internalRef.current!);

  const applyFormatter = (raw: string): string => {
    if (!formatter || lazyFormatter) return raw;
    return formatter(raw);
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const target = e.target;
    const v = applyFormatter(target.value);
    onChange?.(v, e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    const target = e.target;
    let v = target.value;
    if (formatter && lazyFormatter) {
      v = formatter(v);
      onChange?.(v, e);
    }
    onBlur?.(e);
  };

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    onFocus?.(e);
  };

  return (
    <Textarea
      ref={internalRef}
      {...attrs}
      id={id}
      value={modelValue ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
      rows={Number(rows)}
      maxLength={maxLength}
      wrap={wrap}
      aria-label={ariaLabel || attrs['aria-label']}
      aria-invalid={state == null ? attrs['aria-invalid'] : !state}
      autoComplete={autoComplete}
      className={[
        state === false ? 'border-destructive focus-visible:ring-destructive' : state === true ? 'border-green-500 focus-visible:ring-green-500' : '',
        size === 'sm' ? 'text-xs py-1.5' : size === 'lg' ? 'text-lg py-3' : '',
        noResize ? 'resize-none' : '',
        className
      ].filter(Boolean).join(' ')}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
});
